import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, vShow as _vShow, normalizeStyle as _normalizeStyle, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, Teleport as _Teleport, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-a7d3e45a"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "tir-pm-resizable-panel" }
const _hoisted_2 = { class: "tir-pm-resizable-panel__content" }

import {
  computed,
  CSSProperties,
  onMounted,
  onUnmounted,
  PropType,
  ref,
} from "vue";
import type { ResizablePanelSizeSettings } from "./types";
import {
  useRootFontSize,
  useElementObserver,
  useMouseTracking,
} from "tir-pm-toolkit";

// #region defineProps

export default /*#__PURE__*/_defineComponent({
  __name: 'tir-pm-resizable-panel',
  props: {
  /**Настройки размера */
  sizeSettings: {
    type: Object as PropType<Partial<ResizablePanelSizeSettings>>,
    default: (): Partial<ResizablePanelSizeSettings> => ({}),
  },
  /**Разрешен ли рейсайз */
  allowResize: {
    type: Boolean,
    default: true,
  },
  /**Позиция контрола ресайза */
  resizeControlPosition: {
    type: String as PropType<"left" | "right">,
    default: "left",
  },
  /**Запоминать размер при закрытии */
  enableSaveSize: {
    type: Boolean,
    default: false,
  },
  /**Использовать v-show вместо v-if */
  useVShow: {
    type: Boolean,
    default: false,
  },
  /**Фиксированное позиционирование */
  isFixed: {
    type: Boolean,
    default: false
  }
},
  emits: ["onClose", "onOpen"],
  setup(__props, { expose: __expose, emit }) {

const props = __props;


// #endregion defineProps

// #region defineEmits

// #endregion defineEmits

// #region Константы
/**Настройки размера по умолчанию */
const DEFAULT_SIZE_SETTINGS: ResizablePanelSizeSettings = {
  initialWidth: 300,
  initialHeight: 400,
  minHeight: 100,
  minWidth: 100,
  maxHeight: 700,
  maxWidth: 700,
};
// #endregion Константы

// #region refs
/**В состоянии изменения размера */
const isResizing = ref(false);
/**Ссылка на target элемент */
const $target = ref<HTMLElement>();
/**Ссылка на элемент контейнера */
const $container = ref<HTMLElement>();
/**Отслеживание перемещение мыши по экрану */
const { start, stop, offset: mouseOffset, isTracking } = useMouseTracking();
/**Размер корневого шрифта */
const { rootFontSizePx } = useRootFontSize();
/**Размеры body элемента */
const { size: bodySize } = useElementObserver(document.body);
/**Обсервер */
const {
  size: targetSize,
  position: targetPosition,
  updatePositionAndSize,
} = useElementObserver($target);
/**Внутренние настройки размера */
const innerSizeSettings = computed<ResizablePanelSizeSettings>(() =>
  mergeSizeSettings(props.sizeSettings)
);
/**Ширина */
const width = ref(
  addWidthOffset(
    innerSizeSettings.value.initialWidth,
    0,
    innerSizeSettings.value.minWidth,
    innerSizeSettings.value.maxWidth
  )
);
/**Высота */
const height = ref(
  addHeightOffset(
    innerSizeSettings.value.initialHeight,
    0,
    innerSizeSettings.value.minHeight,
    innerSizeSettings.value.maxHeight
  )
);
/**Видимость */
const visible = ref(false);
// #endregion refs

// #region computed
const style = computed<CSSProperties>(() => {
  const _width = isTracking.value
    ? addWidthOffset(
        width.value,
        mouseOffset.value.x,
        innerSizeSettings.value.minWidth,
        innerSizeSettings.value.maxWidth
      )
    : width.value;

  const _height = isTracking.value
    ? addHeightOffset(
        height.value,
        mouseOffset.value.y,
        innerSizeSettings.value.minHeight,
        innerSizeSettings.value.maxHeight
      )
    : height.value;

  const left = Math.max(
    Math.min(targetPosition.value.left, bodySize.value.width - _width),
    0
  );

  const right = Math.min(
    targetPosition.value.right,
    bodySize.value.width - _width
  );

  return {
    top: `calc(${convertToRem(
      targetPosition.value.top + targetSize.value.height,
      rootFontSizePx.value
    )}rem + var(--tir-pm-resizable-panel__top-indent))`,
    left:
      props.resizeControlPosition === "right"
        ? `${convertToRem(left, rootFontSizePx.value)}rem`
        : undefined,
    right:
      props.resizeControlPosition === "left"
        ? `${convertToRem(right, rootFontSizePx.value)}rem`
        : undefined,
    width: `${convertToRem(_width, rootFontSizePx.value)}rem`,
    height: `${convertToRem(_height, rootFontSizePx.value)}rem`,
  };
});
// #endregion computed

// #region Хуки жизненного цикла
onMounted(() => {
  toggleEvents(true);
});

onUnmounted(() => {
  toggleEvents(false);
});
// #endregion Хуки жизненного цикла

// #region Функции
/**Слияние настроек размера */
function mergeSizeSettings(
  customSettings?: Partial<ResizablePanelSizeSettings>
): ResizablePanelSizeSettings {
  return {
    ...DEFAULT_SIZE_SETTINGS,
    ...customSettings,
  };
}
/**Конвертировать пиксели в rem */
function convertToRem(px: number, basePx: number) {
  return px / basePx;
}

/**
 * Добавить смещение в высоту
 * @param currentHeight Текущая высота
 * @param offsetY Смещение по Y
 * @param min Минимальная высота
 * @param max Максимальная высота
 */
function addHeightOffset(
  currentHeight: number,
  offsetY: number,
  min: number,
  max: number
) {
  let _height = currentHeight + offsetY;

  _height = Math.max(_height, min);
  _height = Math.min(_height, max);

  return _height;
}

/**
 * Добавить смещение в ширину
 * @param currentWidth Текущая ширина
 * @param offsetX Смещение по X
 * @param min Минимальная ширина
 * @param max Максимальная ширина
 */
function addWidthOffset(
  currentWidth: number,
  offsetX: number,
  min: number,
  max: number
) {
  let _width =
    props.resizeControlPosition === "left"
      ? currentWidth - offsetX
      : currentWidth + offsetX;

  _width = Math.max(_width, min);
  _width = Math.min(_width, max);

  return _width;
}

/**Переключить класс body при ресайзе */
function toggleResizeBodyClass() {
  const className =
    props.resizeControlPosition === "left"
      ? "tir-pm-resizable-panel__resize-left"
      : "tir-pm-resizable-panel__resize-right";
  if (document.body.classList.contains(className)) {
    document.body.classList.remove(className);
  } else {
    document.body.classList.add(className);
  }
}

/**
 * Проверить клик вне области
 * @param event
 */
function checkClickOutside(event: MouseEvent | TouchEvent) {
  const target = getTargetByEvent(event);
  if (!target) return;

  if (
    visible.value &&
    !$target.value?.contains(target as Node) &&
    !$container.value?.contains(target as Node)
  )
    close();
}
/**Получить target элемент */
function getTargetByEvent(event: MouseEvent | TouchEvent) {
  return event instanceof MouseEvent ? event.target : event.touches[0]?.target;
}
/**При событии mouseup */
function onMouseUp(event: MouseEvent | TouchEvent) {
  // Если не в состоянии перетаскивания проверяем клик вне области
  if (!isResizing.value) {
    checkClickOutside(event);
    return;
  } else {
    stopResize();
  }
}
/**Завершить изменени размера */
function stopResize() {
  if (!isResizing.value) return;
  isResizing.value = false;

  const _width = addWidthOffset(
    width.value,
    mouseOffset.value.x,
    innerSizeSettings.value.minWidth,
    innerSizeSettings.value.maxWidth
  );
  const _height = addHeightOffset(
    height.value,
    mouseOffset.value.y,
    innerSizeSettings.value.minHeight,
    innerSizeSettings.value.maxHeight
  );
  stop();
  width.value = _width;
  height.value = _height;
  toggleResizeBodyClass();
}
/**Начать изменение размера */
function startResize(event: MouseEvent | TouchEvent) {
  if (isResizing.value) return;
  isResizing.value = true;
  start(event);
  toggleResizeBodyClass();
}
/**
 * Подписаться/отсписаться от событий
 * @param isSubscribe Подписаться
 */
function toggleEvents(isSubscribe: boolean) {
  const fnName = isSubscribe ? "addEventListener" : "removeEventListener";
  window[fnName]("mouseup", onMouseUp);
  window[fnName]("touchend", onMouseUp);
}
/**Сбросить размер */
function resetSize() {
  width.value = addWidthOffset(
    innerSizeSettings.value.initialWidth,
    0,
    innerSizeSettings.value.minWidth,
    innerSizeSettings.value.maxWidth
  );
  height.value = addHeightOffset(
    innerSizeSettings.value.initialHeight,
    0,
    innerSizeSettings.value.minHeight,
    innerSizeSettings.value.maxHeight
  );
}
/**Открыть панель */
const open = () => {
  visible.value = true;

  width.value = addWidthOffset(
    width.value,
    0,
    innerSizeSettings.value.minWidth,
    innerSizeSettings.value.maxWidth
  );

  height.value = height.value = addHeightOffset(
    height.value,
    0,
    innerSizeSettings.value.minHeight,
    innerSizeSettings.value.maxHeight
  );

  emit("onOpen");
};
/**Закрыть панель */
const close = () => {
  if (visible.value) {
    visible.value = false;
    if (!props.enableSaveSize) {
      resetSize();
    }
    emit("onClose");
  }
};
// #endregion Функции

// #region defineExpose
__expose({
  /**Открыть панель */
  open,
  /**Закрыть панель */
  close,
  /**Обновить позицию вручную */
  updatePosition: updatePositionAndSize,
  /**Видимость */
  visible
});
// #endregion defineExpose

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "tir-pm-resizable-panel__target",
      ref_key: "$target",
      ref: $target
    }, [
      _renderSlot(_ctx.$slots, "default", {
        open: open,
        close: close,
        visible: visible.value
      })
    ], 512),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createVNode(_Transition, { name: "resizable-panel-transition" }, {
        default: _withCtx(() => [
          (__props.useVShow || visible.value)
            ? _withDirectives((_openBlock(), _createElementBlock("div", {
                key: 0,
                ref_key: "$container",
                ref: $container,
                class: _normalizeClass(['tir-pm-resizable-panel__container', {'is-fixed': __props.isFixed}]),
                style: _normalizeStyle(style.value)
              }, [
                _createElementVNode("div", _hoisted_2, [
                  _renderSlot(_ctx.$slots, "content", {
                    open: open,
                    close: close
                  })
                ]),
                _createElementVNode("div", {
                  class: _normalizeClass([
              'tir-pm-resizable-panel__resize-control',
              `position--${__props.resizeControlPosition}`,
            ]),
                  onMousedown: startResize,
                  onTouchstart: startResize
                }, null, 34)
              ], 6)), [
                [_vShow, !__props.useVShow || visible.value]
              ])
            : _createCommentVNode("", true)
        ]),
        _: 3
      })
    ]))
  ]))
}
}

})